import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Box, Typography, Alert, CircularProgress,
  IconButton, Tooltip, Grid, Chip, Divider, Autocomplete, MenuItem,
  ToggleButtonGroup, ToggleButton, FormControlLabel, Checkbox,
} from '@mui/material';
import { fetchVenues } from '../store/slices/venuesSlice';
import { CANONICAL_ORIGIN } from '../config';
import {
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { createPackage, createShare } from '../store/slices/deliverySlice';
import EmailComposer from './EmailComposer';

/**
 * Reusable dialog for creating a delivery package + share link.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   sourceType: "screener" | "dcp"
 *   sourceJobId: number
 *   projectName: string
 *   filename: string
 *   nasPath: string - full path to file on NAS
 *   recipientName?: string
 *   recipientEmail?: string
 */
function buildDcpMessage(jobData, recipientName, userName) {
  if (!jobData) return '';
  const name = recipientName || 'there';
  const std = jobData.standard || 'SMPTE';
  const res = jobData.resolution || '2K';
  const fps = jobData.frame_rate || 24;
  const audio = jobData.audio_layout || '5.1';
  const channels = jobData.audio_channels || 8;
  const encrypted = jobData.encrypt !== false ? 'Yes' : 'No';
  const qc = jobData.qc_status === 'pass' ? 'QC passed' : jobData.qc_status === 'warn' ? 'QC passed with warnings' : '';
  return [
    `Hi ${name},`,
    '',
    `Please find your DCP for "${jobData.name}" available for download at the link below.`,
    '',
    'Specifications:',
    `  • Standard: ${std}`,
    `  • Resolution: ${res}`,
    `  • Frame Rate: ${fps} fps`,
    `  • Audio: ${audio} (${channels}ch)`,
    `  • Encrypted: ${encrypted}`,
    qc ? `  • ${qc}` : '',
    '',
    'A QC report is included inside the DCP package as qc_report.txt.',
    '',
    'Please confirm receipt at your earliest convenience.',
    '',
    `Best,`,
    userName || '',
  ].filter((l, i, a) => !(l === '' && a[i - 1] === '')).join('\n');
}

export default function DeliverDialog({
  open, onClose, sourceType, sourceJobId, projectName, filename, nasPath,
  recipientName: defaultRecipient = '', recipientEmail: defaultEmail = '',
  jobData, defaultPhysical = false,
}) {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.auth);
  const { venues } = useSelector((s) => s.venues);
  const { packages } = useSelector((s) => s.delivery);
  useEffect(() => { if (open && (!venues || venues.length === 0)) dispatch(fetchVenues()); }, [open, dispatch]);
  const [compatWarnings, setCompatWarnings] = useState([]);
  const checkVenueCompat = async (venue) => {
    if (!venue || sourceType !== 'dcp' || !sourceJobId) {
      setCompatWarnings([]);
      return;
    }
    const screenIds = (venue.screens || []).filter(s => s.is_active !== false).map(s => s.id);
    if (screenIds.length === 0) {
      setCompatWarnings([]);
      return;
    }
    try {
      const api = (await import('../services/api')).default;
      const r = await api.post('/api/equipment/check-compatibility', {
        dcp_job_id: sourceJobId,
        screen_ids: screenIds,
      });
      setCompatWarnings(r.data?.warnings || []);
    } catch (_e) { setCompatWarnings([]); }
  };
  const venueOptions = venues || [];
  const [step, setStep] = useState('form'); // form, creating, done
  const [recipientName, setRecipientName] = useState(defaultRecipient);
  const [recipientEmail, setRecipientEmail] = useState(defaultEmail);
  const initialCc = userInfo?.email || '';
  const initialMsg = (sourceType === 'dcp' && jobData)
    ? buildDcpMessage(jobData, defaultRecipient, userInfo?.full_name || userInfo?.username || '')
    : '';
  const [ccEmail, setCcEmail] = useState(initialCc);
  const [physicalDelivery, setPhysicalDelivery] = useState(defaultPhysical);
  // Physical delivery (USB drive on a Pi node). When the operator picks a
  // node, "Stage to drive" replaces the digital-share submit path with an
  // automated rsync onto the drive. Nodes load lazily — if the user isn't
  // an operator, the GET 403s and we silently fall back to the existing
  // manual download path.
  const [physicalNodes, setPhysicalNodes] = useState([]);
  const [physicalNodeId, setPhysicalNodeId] = useState(null);
  const [physicalNodeStatus, setPhysicalNodeStatus] = useState(null);
  // Extract the archive on the drive after rsync — projector media servers
  // ingest the DCP folder, not the zip. Default ON for DCPs.
  const [extractOnDrive, setExtractOnDrive] = useState(true);
  const [expiresDays, setExpiresDays] = useState(7);
  const [speedLimit, setSpeedLimit] = useState('');
  // Venue delivery profile — fetched when an Autocomplete venue is
  // picked. The profile drives the format default + a "what we know"
  // banner that warns the operator when there's no history (so they
  // ask the venue what they prefer instead of guessing).
  const [venueProfile, setVenueProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [pickedVenueId, setPickedVenueId] = useState(null);
  // Archive format the operator wants to ship. Defaults to .zip
  // (universal extraction); operator can override per-delivery.
  const [deliveryFormat, setDeliveryFormat] = useState('zip');
  const [description, setDescription] = useState(initialMsg);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  // Track the share id created in this dialog so the success state can
  // wire the EmailComposer against it.
  const [lastShareId, setLastShareId] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  // Lazy-load physical delivery nodes when the operator flips to physical mode.
  useEffect(() => {
    if (!open || !physicalDelivery || sourceType !== 'dcp') return;
    if (physicalNodes.length > 0) return;
    (async () => {
      try {
        const api = (await import('../services/api')).default;
        const r = await api.get('/api/physical-delivery/nodes');
        const list = (r.data || []).filter((n) => n.is_active);
        setPhysicalNodes(list);
        if (list.length && !physicalNodeId) setPhysicalNodeId(list[0].id);
      } catch (_e) {
        // 401/403 (non-operator) or missing endpoint — keep the legacy
        // "manual download" path available.
        setPhysicalNodes([]);
      }
    })();
  }, [open, physicalDelivery, sourceType, physicalNodes.length, physicalNodeId]);

  useEffect(() => {
    if (!physicalDelivery || !physicalNodeId) { setPhysicalNodeStatus(null); return; }
    (async () => {
      try {
        const api = (await import('../services/api')).default;
        const r = await api.get(`/api/physical-delivery/nodes/${physicalNodeId}/status`);
        setPhysicalNodeStatus(r.data);
      } catch (_e) {
        setPhysicalNodeStatus(null);
      }
    })();
  }, [physicalDelivery, physicalNodeId]);

  const handleStageToDrive = async () => {
    setStep('creating');
    setError(null);
    try {
      const api = (await import('../services/api')).default;
      // Reuse an existing package if one is already synced for this DCP.
      let pkg = (packages || []).find(p =>
        p.source_job_id === sourceJobId && p.source_type === sourceType && !p.revoked
      );
      if (!pkg) {
        pkg = await dispatch(createPackage({
          project_name: projectName,
          source_type: sourceType,
          source_job_id: sourceJobId,
          nas_source_path: nasPath,
          filename,
          description: description || undefined,
        })).unwrap();
      }
      await api.post('/api/physical-delivery/deliveries', {
        package_id: pkg.id,
        node_id: physicalNodeId,
        venue_id: pickedVenueId || undefined,
        ship_to_name: recipientName || undefined,
        notes: description || undefined,
        extract: extractOnDrive,
      });
      setStep('done');
      setDownloadUrl('');  // no link — staging is the artifact
    } catch (err) {
      setError(err.response?.data?.detail || (typeof err === 'string' ? err : 'Failed to start staging'));
      setStep('form');
    }
  };

  const handleDeliver = async () => {
    setStep('creating');
    setError(null);

    try {
      // Step 1: Use existing pre-synced package if available, otherwise create
      const existingPkgs = packages || [];
      let pkg = (existingPkgs || []).find(p =>
        p.source_job_id === sourceJobId && p.source_type === sourceType && !p.revoked
      );
      if (!pkg) {
        pkg = await dispatch(createPackage({
          project_name: projectName,
          source_type: sourceType,
          source_job_id: sourceJobId,
          nas_source_path: nasPath,
          filename: filename,
          description: description || undefined,
        })).unwrap();
      }

      // Step 2: Create share — skip email for physical delivery
      const share = await dispatch(createShare({
        package_id: pkg.id,
        recipient_name: physicalDelivery ? (userInfo?.full_name || 'Physical Delivery') : (recipientName || undefined),
        recipient_email: physicalDelivery ? undefined : (recipientEmail || undefined),
        cc_email: physicalDelivery ? undefined : (ccEmail || undefined),
        expires_days: physicalDelivery ? 30 : expiresDays,
        speed_limit_mbps: speedLimit ? parseInt(speedLimit) : undefined,
      })).unwrap();

      setDownloadUrl(share.download_url || `${CANONICAL_ORIGIN}/d/${share.token}`);
      setLastShareId(share.id);
      setStep('done');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to create delivery');
      setStep('form');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(downloadUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setStep('form');
    setDownloadUrl('');
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Deliver: {projectName}
        <Typography variant="body2" color="text.secondary">{filename}</Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {step === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Physical-vs-digital and venue picker only apply to DCPs going
                to theatres. Watermark screeners are always digital and the
                recipient is a press/awards contact, not a venue. */}
            {sourceType === 'dcp' && (
              <ToggleButtonGroup
                value={physicalDelivery ? 'physical' : 'digital'}
                exclusive
                onChange={(_, v) => { if (v) setPhysicalDelivery(v === 'physical'); }}
                size="small"
                fullWidth
              >
                <ToggleButton value="digital">Digital Delivery</ToggleButton>
                <ToggleButton value="physical">Physical Delivery</ToggleButton>
              </ToggleButtonGroup>
            )}

            {sourceType === 'dcp' && physicalDelivery && physicalNodes.length === 0 && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                No physical delivery nodes are registered. A download link will be created for you to fetch the file and transfer to a drive yourself.
              </Alert>
            )}

            {sourceType === 'dcp' && physicalDelivery && physicalNodes.length > 0 && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Stage to USB drive</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  {physicalNodes.map((n) => (
                    <Chip key={n.id} label={n.name}
                          color={n.id === physicalNodeId ? 'primary' : 'default'}
                          variant={n.id === physicalNodeId ? 'filled' : 'outlined'}
                          onClick={() => setPhysicalNodeId(n.id)} />
                  ))}
                </Box>
                {physicalNodeStatus ? (
                  physicalNodeStatus.mounted ? (
                    <Typography variant="caption" color="text.secondary">
                      Drive ready · {physicalNodeStatus.fs} ·{' '}
                      {((physicalNodeStatus.free_bytes || 0) / (1024 ** 3)).toFixed(1)} GB free of{' '}
                      {((physicalNodeStatus.size_bytes || 0) / (1024 ** 3)).toFixed(1)} GB
                    </Typography>
                  ) : physicalNodeStatus.disk ? (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      Drive detected but not mounted. Go to Operator · Physical Drives to mount it before staging.
                    </Alert>
                  ) : (
                    <Alert severity="warning" sx={{ py: 0 }}>No drive plugged in on this node.</Alert>
                  )
                ) : (
                  <Typography variant="caption" color="text.secondary">Checking drive…</Typography>
                )}
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Checkbox size="small" checked={extractOnDrive}
                                     onChange={(e) => setExtractOnDrive(e.target.checked)} />}
                  label={<Typography variant="caption">
                    Extract on drive (DCP folder ready for projector ingest — uncheck to ship the archive instead)
                  </Typography>}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  "Stage to Drive" rsyncs the package from the NAS onto the drive. No email is sent; the operator ships the drive physically after staging finishes.
                </Typography>
              </Box>
            )}

            {sourceType === 'dcp' && !physicalDelivery && <Autocomplete
              options={venueOptions}
              getOptionLabel={(v) => `${v.chain ? v.chain + ' — ' : ''}${v.name}${v.city ? ' (' + v.city + ')' : ''}`}
              onChange={(_, v) => {
                if (!v) {
                  setVenueProfile(null);
                  setPickedVenueId(null);
                  return;
                }
                const vName = v.tech_contact_name || '';
                setRecipientName(vName);
                setRecipientEmail(v.tech_contact_email || '');
                checkVenueCompat(v);
                if (sourceType === 'dcp' && jobData) {
                  const userName = userInfo?.full_name || userInfo?.username || '';
                  setDescription(buildDcpMessage(jobData, vName, userName));
                }
                // Pull the delivery-profile summary so we can default the
                // archive format to whatever's worked for this venue
                // before, and warn the operator if there's no history.
                setPickedVenueId(v.id);
                setVenueProfile(null);
                setProfileLoading(true);
                (async () => {
                  try {
                    const api = (await import('../services/api')).default;
                    const r = await api.get(`/api/venues/${v.id}/profile-summary`);
                    setVenueProfile(r.data);
                    if (r.data?.recommended_format) {
                      setDeliveryFormat(r.data.recommended_format);
                    }
                  } catch (_e) {
                    setVenueProfile(null);
                  } finally {
                    setProfileLoading(false);
                  }
                })();
              }}
              renderInput={(params) => (
                <TextField {...params} label="Pick an existing venue (optional)"
                  helperText="Selecting a venue fills the recipient name and email below. You can also type them manually." />
              )}
            />}

            {/* Venue delivery profile — what we know about how prior
                deliveries to this venue went. Drives the format default
                and surfaces "ask first" prompts when there's no history. */}
            {sourceType === 'dcp' && !physicalDelivery && pickedVenueId && (
              <Box sx={{
                p: 1.5, mt: -1, mb: 0,
                border: 1, borderColor: 'divider', borderRadius: 1,
                bgcolor: 'background.default',
              }}>
                <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', fontSize: '0.7rem', mb: 1 }}>
                  What we know about this venue
                </Typography>
                {profileLoading && (
                  <Typography variant="caption" color="text.secondary">Loading profile…</Typography>
                )}
                {!profileLoading && venueProfile && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                      <Chip
                        size="small"
                        label={`Recommended: .${venueProfile.recommended_format}`}
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {venueProfile.rationale}
                      </Typography>
                    </Box>
                    {(venueProfile.flags || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {venueProfile.flags.includes('no_known_format_ask_first') && (
                          <Chip size="small" color="warning" variant="outlined"
                            label="Ask first — no prior delivery to this venue" />
                        )}
                        {venueProfile.flags.includes('unresponsive') && (
                          <Chip size="small" color="warning" variant="outlined"
                            label="Historically unresponsive — pre-call recommended" />
                        )}
                        {venueProfile.flags.filter(f => f.startsWith('last_attempt_issue:')).map((f) => (
                          <Chip key={f} size="small" color="error" variant="outlined"
                            label={`Last attempt: ${f.split(':')[1].replace(/_/g, ' ')}`} />
                        ))}
                      </Box>
                    )}
                    {(venueProfile.recent || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Recent deliveries
                        </Typography>
                        {venueProfile.recent.slice(-3).reverse().map((o) => (
                          <Typography key={o.id} variant="caption" sx={{ fontSize: '0.72rem' }}>
                            <Box component="span" sx={{ color: 'text.secondary' }}>{new Date(o.created_at).toLocaleDateString()}</Box>
                            {' · '}
                            <Box component="span" sx={{ fontWeight: 500 }}>
                              .{o.format_used || '?'}
                            </Box>
                            {' → '}
                            <Box component="span" sx={{
                              color: o.outcome === 'success_clean' ? 'success.main'
                                : ['format_unknown','download_failed','kdm_filter_blocked','av_issue'].includes(o.outcome) ? 'error.main'
                                : 'warning.main',
                            }}>
                              {o.outcome.replace(/_/g, ' ')}
                            </Box>
                            {o.notes && (
                              <Box component="span" sx={{ color: 'text.secondary' }}> — {o.notes.slice(0, 80)}{o.notes.length > 80 ? '…' : ''}</Box>
                            )}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {(venueProfile.recent || []).length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No prior delivery history. Worth asking the venue tech contact what format and KDM-delivery method they prefer before sending.
                      </Typography>
                    )}
                  </>
                )}

                {/* Format picker — defaults to recommended. Changing
                    the format on a DCP that's already packaged triggers
                    a re-package via the backend (clears package_path,
                    flips delivery_format; the packaging loop re-runs).
                    The current package keeps serving until the new
                    one syncs, so no delivery interruption. */}
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Send as:</Typography>
                  <ToggleButtonGroup
                    value={deliveryFormat}
                    exclusive
                    size="small"
                    onChange={(_, v) => { if (v) setDeliveryFormat(v); }}
                  >
                    <ToggleButton value="zip" sx={{ px: 2, py: 0.5, fontSize: '0.75rem' }}>.zip</ToggleButton>
                    <ToggleButton value="tar" sx={{ px: 2, py: 0.5, fontSize: '0.75rem' }}>.tar</ToggleButton>
                  </ToggleButtonGroup>
                  {venueProfile && deliveryFormat !== venueProfile.recommended_format && (
                    <Typography variant="caption" color="warning.main">
                      overriding recommendation
                    </Typography>
                  )}
                  {sourceType === 'dcp' && jobData && deliveryFormat !== (jobData.delivery_format || 'tar') && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={async () => {
                        try {
                          const api = (await import('../services/api')).default;
                          await api.post(`/api/dcp-jobs/${sourceJobId}/repackage?format=${deliveryFormat}`);
                          setError(null);
                          // Show a friendly note rather than an error.
                          alert(`Re-package queued as .${deliveryFormat}. The packaging loop picks it up within ~30s; current package keeps serving until the new one syncs.`);
                        } catch (e) {
                          setError(e.response?.data?.detail || `Failed to re-package: ${e.message}`);
                        }
                      }}
                    >
                      Re-package as .{deliveryFormat}
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {!physicalDelivery && compatWarnings.length > 0 && (
              <Alert
                severity={compatWarnings.some(w => w.issues.some(i => i.severity === 'error')) ? 'error' : 'warning'}
                sx={{ mt: 1 }}
              >
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {compatWarnings.some(w => w.issues.some(i => i.severity === 'error'))
                    ? 'Heads up: this DCP may not play on this venue'
                    : 'Compatibility notes'}
                </Typography>
                {compatWarnings.map(w => (
                  <Box key={w.screen_id} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" fontWeight={600}>
                      {w.screen_name}
                    </Typography>
                    {w.issues.map((i, idx) => (
                      <Typography key={idx} variant="caption" component="div" sx={{ ml: 1 }}>
                        <strong>{i.severity === 'error' ? '✗' : '⚠'}</strong> {i.title} — {i.detail}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Alert>
            )}
            {!physicalDelivery && (<>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Recipient Name"
                  fullWidth value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="e.g. John Smith"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Recipient Email(s)"
                  fullWidth value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="john@studio.com, jane@studio.com"
                  helperText="Comma-separate for multiple. Email auto-sent on create."
                />
              </Grid>
            </Grid>
            <TextField
              label="CC"
              fullWidth value={ccEmail}
              onChange={(e) => setCcEmail(e.target.value)}
              placeholder="producer@studio.com, coordinator@studio.com"
              helperText="Optional. Comma-separate for multiple CC addresses."
              InputLabelProps={{ shrink: Boolean(ccEmail) || undefined }}
            />
            </>)}

            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="Link Expires (days)"
                  type="number" fullWidth
                  value={expiresDays}
                  onChange={(e) => setExpiresDays(parseInt(e.target.value) || 7)}
                  helperText="Default: 7 days"
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="Speed Limit"
                  select fullWidth
                  value={speedLimit}
                  onChange={(e) => setSpeedLimit(e.target.value)}
                  helperText="Limit download bandwidth per recipient"
                >
                  <MenuItem value="">Unlimited</MenuItem>
                  <MenuItem value="1000">1 Gbps</MenuItem>
                  <MenuItem value="500">500 Mbps</MenuItem>
                  <MenuItem value="250">250 Mbps</MenuItem>
                  <MenuItem value="100">100 Mbps</MenuItem>
                </TextField>
              </Grid>
            </Grid>

            {!physicalDelivery && (
            <TextField
              label="Message"
              fullWidth multiline rows={10}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Final master for review"
              InputLabelProps={{ shrink: Boolean(description) || undefined }}
              sx={{ '& .MuiOutlinedInput-root': { height: 'auto !important' } }}
            />
            )}
          </Box>
        )}

        {step === 'creating' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Creating delivery package...</Typography>
          </Box>
        )}

        {step === 'done' && !downloadUrl && physicalDelivery && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Physical delivery requested.</strong> An HHMG operator will
              connect a drive and begin staging your DCP. You'll receive an email
              when the drive is ready to be shipped.
            </Alert>
          </Box>
        )}

        {step === 'done' && downloadUrl && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {physicalDelivery
                ? 'Download link ready. Use the link below to download the file to your drive.'
                : 'Download link created! The file will be available once it syncs to the delivery server.'}
            </Alert>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Download Link</Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              p: 1.5, borderRadius: 1,
              bgcolor: 'action.hover',
              fontFamily: 'monospace',
            }}>
              <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {downloadUrl}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <IconButton size="small" onClick={handleCopy}>
                  <CopyIcon fontSize="small" color={copied ? 'success' : 'action'} />
                </IconButton>
              </Tooltip>
            </Box>

            {recipientEmail && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  ✉️ Email sent to: <strong>{recipientEmail}</strong>
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Chip label={`Expires in ${expiresDays} days`} size="small" variant="outlined" />
              <Chip label={sourceType === 'dcp' ? 'DCP' : 'Watermark Encode'} size="small" variant="outlined" />
            </Box>

            {/* Compose follow-up email — pre-filled with checksum, KDM
                info, mix notes, ingest help. Operator subtractively
                edits and sends via their own mail client. Wired to the
                share we just created so the template renders against
                the right context. */}
            {lastShareId && !physicalDelivery && (
              <Box sx={{ mt: 2.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary', fontSize: '0.72rem' }}>
                  Want to send a richer follow-up than the auto-email — checklist for confirmation, ingest help, mix notes, fallback options? Pull a template:
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EmailIcon fontSize="small" />}
                  onClick={() => setComposerOpen(true)}
                >
                  Compose follow-up email
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{step === 'done' ? 'Close' : 'Cancel'}</Button>
        {step === 'form' && (
          (sourceType === 'dcp' && physicalDelivery && physicalNodeId && physicalNodeStatus?.mounted) ? (
            <Button variant="contained" color="primary" onClick={handleStageToDrive} startIcon={<SendIcon />}>
              Stage to Drive
            </Button>
          ) : (
            <Button variant="contained" onClick={handleDeliver} startIcon={<SendIcon />}>
              Create Download Link
            </Button>
          )
        )}
      </DialogActions>

      {composerOpen && lastShareId && (
        <EmailComposer
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          touchpoint={sourceType === 'dcp' ? 'dcp_delivery' : 'screener_delivery'}
          subjectId={lastShareId}
          contextLabel={`${recipientName || recipientEmail || 'recipient'} · ${projectName || filename}`}
        />
      )}
    </Dialog>
  );
}
